import type { ReactNode } from "react";
import { SwipeableDrawer } from "@mui/material";
import { RemoveScroll } from "react-remove-scroll";
import { IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { AnnouncementPresentation } from "../../../../api/src/shared/domain/announcement";
import { Button } from "../../components/ui/Button";
import { cn } from "../../lib/utils";
import { isIOSApp } from "../../tools/ios";
import { useIsDesktopView } from "../../common/hooks/useIsDesktopView";
import type { AnnouncementDismissMethod } from "./types";

/**
 * One button in the footer. `variant` is an intent, not a colour: "primary" and
 * "outline" are the DS variants of the same name, and "healthy-hour" is the
 * primary button in the green of the Healthy Hour webinar series, so a webinar
 * announcement's call to action reads as the event its hero is dressed in.
 */
export type AnnouncementAction = {
  label: string;
  variant: "primary" | "outline" | "healthy-hour";
  onPress: () => void;
};

/**
 * The DS button behind each intent. Healthy Hour rides the primary variant and
 * only swaps the base colour token it paints with, so it keeps the hover and
 * pressed overlays, the disabled state, and the focus ring of every other CTA.
 */
function actionButtonProps(variant: AnnouncementAction["variant"]): {
  variant: "primary" | "outline";
  className: string | null;
} {
  switch (variant) {
    case "primary":
      return { variant: "primary", className: null };
    case "outline":
      return { variant: "outline", className: null };
    case "healthy-hour":
      return {
        variant: "primary",
        className:
          "[--ds-btn-base:var(--color-ds-healthy-hour)] border-ds-healthy-hour",
      };
    default:
      throw new Error(variant satisfies never);
  }
}

/**
 * The chrome every pop-up announcement shares: the hero, the grab handle, the
 * close button, the chip, the heading, and the footer that pins the actions
 * above the safe area.
 *
 * Announcements are CODE — each is a component under this directory, built from
 * a Figma frame — so this shell exists to stop the parts that must never differ
 * between them being re-typed each time: the geometry, the scroll behaviour, the
 * dialog semantics, and the way the header floats over artwork. Everything an
 * announcement actually says lives in the component that renders through it.
 *
 * `presentation` picks the container. A sheet rises from the bottom edge and can
 * be swiped away (as `ShareSheet` and `ChatSourceSheet` do); a modal sits
 * centred. Same content, different frame, which is why it is a prop and not two
 * components.
 */
export function AnnouncementShell({
  presentation: requestedPresentation,
  maxWidth,
  isOpen,
  onDismiss,
  media,
  label,
  title,
  actions,
  features,
}: {
  presentation: AnnouncementPresentation;
  /**
   * How wide the announcement may grow past a phone: the centred desktop modal
   * (560px) and the sheet on a tablet webview (600px). "wide" is for the
   * feature tours, whose hero has a fixed height and crops gracefully as the
   * frame widens. "phone" holds both at the 393px every frame is designed at,
   * for a poster hero that has to be shown whole: it keeps its ratio, so at
   * 560px it stands 390px tall and pushes the buttons under the fold.
   */
  maxWidth: "wide" | "phone";
  isOpen: boolean;
  /**
   * The reader left without acting. The shell can tell the close button from
   * the backdrop and the swipe, and nothing else can, so it reports which —
   * "tapped close" and "swiped it away" are different signals.
   */
  onDismiss: (method: AnnouncementDismissMethod) => void;
  /** Full-bleed artwork above the heading. Null opens straight on the title. */
  media: ReactNode | null;
  /** The small chip above the heading ("New"). Null shows no chip. */
  label: string | null;
  title: string;
  actions: readonly AnnouncementAction[];
  /**
   * The feature rows. Null for an announcement that is only a poster, and an
   * explicit null rather than an omitted child: `ReactNode` admits undefined
   * and an empty fragment too, and both would still render the padded box.
   */
  features: ReactNode | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { isDesktop } = useIsDesktopView();

  // A bottom sheet is a phone idiom. On a desktop browser it rises out of the
  // bottom edge of a 1400px window and reads as a mistake, so a sheet becomes a
  // centred modal there. `presentation` is therefore what the announcement asks
  // for on a PHONE; an announcement that asks for a modal gets one everywhere.
  // A native shell is desktop only on a large tablet (`useIsDesktopView`), so
  // the phone webviews this is mostly seen in keep the sheet.
  const presentation: AnnouncementPresentation =
    requestedPresentation === "sheet" && isDesktop
      ? "modal"
      : requestedPresentation;

  const body = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className={cn(
        // ONE scroll container, and it is the dialog itself. The artwork and the
        // footer scroll with the body rather than pinning it between them: a
        // sheet that scrolls only in its middle reads as a page inside a page,
        // and on a short screen it leaves the reader a narrow band to read
        // through. `overflow-y-auto` still clips to the rounded corners, so the
        // hero keeps its shape.
        //
        // No scrollbar, the same way every other scrolling surface in the app
        // suppresses one (`DiscoveryTabBar`, `PracticeFeedRail`, the GPT
        // prompts). Desktop draws a track down the full height of the sheet,
        // over the artwork, and it is there whether or not anything overflows.
        // What tells the reader there is more is the content running past the
        // fold, which is what the design does anyway.
        "bg-ds-white w-full overflow-y-auto scrollbar-none [&::-webkit-scrollbar]:hidden",
        // The same ceiling for both: a modal that stops at 86dvh scrolled its
        // last button off a 780px laptop viewport, and 4% of margin is enough
        // to read as floating.
        presentation === "sheet"
          ? "rounded-t-4xl max-h-[92dvh]"
          : "rounded-2xl max-h-[92dvh]"
      )}
    >
      {/* STICKY, not absolute. The whole sheet scrolls, and the close button is
          the one control the reader must always be able to reach, so it holds
          the top-right corner however far they scroll.

          Over artwork it takes NO height, so the hero still runs full-bleed into
          the top corners and the button paints over it. With no artwork it keeps
          its own height and an opaque background: zero height there would drop
          the button on the title, and a transparent one would let the copy
          scroll underneath it. */}
      <div
        className={cn(
          "sticky top-0 z-10",
          media === null ? "bg-ds-white" : "h-0"
        )}
      >
        {/* The padding lives on the INNER box. `h-0` is a border-box height, so
            padding on the sticky element itself cannot be compressed away: it
            kept 8px of layout, pushed the artwork down, and left a white band
            across the top of the sheet. Zero-height outside, real padding
            inside, and the hero starts at the very top edge again. */}
        <div className="flex flex-col items-center gap-2 px-6 pt-2">
          {presentation === "sheet" && (
            // A dark grip vanishes over dark artwork, so its colour follows
            // what is behind it.
            <div
              className={cn(
                "h-1 w-10 rounded-full",
                media === null ? "bg-ds-slate-300" : "bg-ds-white/70"
              )}
            />
          )}
          <div className="flex w-full justify-end">
            <button
              type="button"
              data-testid="dismiss-modal"
              aria-label={t("Close")}
              onClick={() => {
                onDismiss("close_button");
              }}
              className="flex size-11 cursor-pointer items-center justify-center rounded-full border-0 bg-ds-white/50 backdrop-blur-[2px]"
            >
              <IconX size={24} stroke={2} className="text-ds-mint-900" />
            </button>
          </div>
        </div>
      </div>

      {media}

      {/* Artwork butts straight into the chip without the top padding: both
          frames put 24px between the hero and the chip (252→276 on the feed,
          274→298 on the GPT one). With no artwork the header above is in normal
          flow and already carries the handle and a 44px close button, so the
          same padding there would be twice the gap the design asks for. */}
      <div
        className={cn(
          "flex flex-col gap-3 px-6 pb-6",
          media === null ? null : "pt-6"
        )}
      >
        {label !== null && (
          <span className="self-start rounded-xl bg-ds-moss-100 px-2 py-1 font-dm-sans text-footnote font-medium text-ds-mint-700">
            {label}
          </span>
        )}

        {/* `whitespace-pre-line`: a title may carry a line break of its own, the
            way the Healthy Hour frame drops "Live in English." onto its own
            line. The locale files already hold keys with `\n` in them, so the
            break travels with the copy rather than being a second prop. */}
        <h2 className="m-0 font-dm-sans text-heading-lg font-semibold whitespace-pre-line text-ds-mint-900">
          {title}
        </h2>

        {/* No rows, no box: an empty one still spends its padding and its
            share of the gap, 28px a poster has no use for. */}
        {features !== null && (
          <div className="flex flex-col gap-6 py-2">{features}</div>
        )}
      </div>

      {/* The sheet's 32px keeps the buttons clear of the phone's home indicator
          on top of the safe-area padding the paper adds. A desktop modal has
          neither, so it closes with the same 20px it opens with. */}
      <div
        className={cn(
          "border-t border-ds-black/6 px-5 pt-3",
          presentation === "sheet" ? "pb-8" : "pb-5"
        )}
      >
        <div className="flex flex-col gap-3">
          {actions.map((action) => {
            const button = actionButtonProps(action.variant);
            return (
              <Button
                key={action.label}
                variant={button.variant}
                size="md"
                className={cn("w-full", button.className)}
                onClick={action.onPress}
              >
                {action.label}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // A centred modal still rides the drawer, so both presentations inherit the
  // same backdrop, focus handling and close-on-backdrop behaviour; only the
  // paper's position differs.
  const paperSx =
    presentation === "modal"
      ? {
          backgroundColor: "transparent",
          boxShadow: "none",
          top: "50%",
          bottom: "auto",
          left: "50%",
          right: "auto",
          // Centred with `translate`, never `transform`. The drawer's own slide
          // transition writes `transform` inline while it animates, so a
          // `translateY(-50%)` here is overwritten the moment it opens and the
          // modal hangs with its TOP edge on the middle of the screen. The
          // `translate` property composes with that transform instead of
          // fighting it.
          translate: "-50% -50%",
          width: "calc(100% - 40px)",
          maxWidth: maxWidth === "wide" ? "560px" : "393px",
        }
      : {
          backgroundColor: "var(--color-ds-white)",
          borderTopLeftRadius: 32,
          borderTopRightRadius: 32,
          paddingBottom: "env(safe-area-inset-bottom)",
          maxHeight: "calc(100dvh - env(safe-area-inset-top) - 8px)",
          "@media (min-width: 768px)": {
            left: "50% !important",
            right: "auto !important",
            transform: "translateX(-50%) !important",
            maxWidth: maxWidth === "wide" ? "600px" : "393px",
            width: maxWidth === "wide" ? "600px" : "393px",
          },
        };

  return (
    <SwipeableDrawer
      anchor="bottom"
      disableSwipeToOpen
      disableBackdropTransition={!isIOSApp()}
      open={isOpen}
      onOpen={() => {}}
      onClose={() => {
        onDismiss("backdrop_or_swipe");
      }}
      sx={{ "& .MuiDrawer-paper": paperSx }}
    >
      <RemoveScroll enabled={isOpen}>{body}</RemoveScroll>
    </SwipeableDrawer>
  );
}

/**
 * One "what this does" row: a tile-framed glyph, a mint heading and a grey
 * explanation. Both shipped announcements are a stack of these, and the Figma
 * frames draw them identically (32px tile, 14px gap, 16/13px type).
 */
export function AnnouncementFeature({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: string;
}): React.JSX.Element {
  return (
    <div className="flex w-full items-start gap-3.5">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-ds-black/6 text-ds-mint-800">
        {icon}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="m-0 font-dm-sans text-base/[1.4] font-bold text-ds-mint-800">
          {title}
        </p>
        <p className="m-0 font-dm-sans text-footnote/[1.48] text-ds-slate-600">
          {description}
        </p>
      </div>
    </div>
  );
}
